import { useQuery } from '@apollo/client'
import {
    MenuItem,
    Paper,
    Select,
    Stack,
    Tab,
    Tabs,
    useMediaQuery,
} from '@mui/material'
import { ProcessStatusGroup } from '__generated__/graphql'
import theme from 'candidate/theme'
import ClientPortalActionsCell from 'client/components/ClientPortalActionsCell'
import { GetRosterCandidateStatusGroupCounts } from 'client/graphql/search.gql'
import React from 'react'
import { FilterSidebar } from 'client/candidate-table-temp/components/Filters'
import { RosterCandidatesTablePaginated } from 'client/candidate-table-temp/components/RosterCandidatesTable'
import { RosterCandidateColumnsProvider } from 'client/candidate-table-temp/contexts/RosterCandidatesColumnsContext'
import { BulkActionContextProvider } from 'client/candidate-table-temp/contexts/BulkActionContext'
import { useRosterSearchWithFilters } from 'client/candidate-table-temp/hooks/useRosterSearchWithFilters'
import { useTabLabels } from 'client/candidate-table-temp/hooks/useTabLabels'
import { getPxAsRem } from 'shared/utils'
import { ClientRosterFilters } from './ClientRosterFilters'

export interface CandidatesTableProps {
    rosterId: string
}
const CandidatesSearchTable: React.FC<CandidatesTableProps> = ({
    rosterId,
}) => {
    const isSmScreen = useMediaQuery(theme.breakpoints.down('sm'))

    const {
        page,
        limit,
        filters,
        hasActiveFilters,
        data: sqlData,
        loading: sqlLoading,
        totalItems,
        currentView,
        setPage,
        setLimit,
        setFilters,
        clearFilters,
    } = useRosterSearchWithFilters({
        rosterId,
    })

    const changeTab = (newValue: ProcessStatusGroup) => {
        setFilters({
            ...filters,
            processStatusGroup: newValue,
        })
    }

    const { data: groupCountsData } = useQuery(
        GetRosterCandidateStatusGroupCounts,
        {
            variables: {
                rosterId: rosterId ?? '',
                summaryGroups: [
                    ProcessStatusGroup.Submitted,
                    ProcessStatusGroup.InProcess,
                    ProcessStatusGroup.OutOfProcess,
                ],
            },
            skip: !rosterId,
            onError: (error) => {
                console.error(
                    'Error fetching candidate status group counts:',
                    error
                )
            },
        }
    )

    const { getTabLabel } = useTabLabels({
        groupCounts: groupCountsData?.getRosterCandidateStatusGroupCounts || [],
        totalItems,
        activeTab: filters.processStatusGroup,
    })

    return (
        <BulkActionContextProvider>
            <RosterCandidateColumnsProvider
                currentView={currentView}
                rosterId={rosterId}
                canEdit={false}
                ActionsCell={ClientPortalActionsCell}
            >
                <Stack
                    direction="row"
                    gap={getPxAsRem(24)}
                    width="100%"
                    height="300px"
                    flexGrow={1}
                >
                    <FilterSidebar
                        hasActiveFilters={hasActiveFilters}
                        onClearFilters={clearFilters}
                    >
                        <ClientRosterFilters
                            filters={filters}
                            setFilters={setFilters}
                        />
                    </FilterSidebar>

                    <Stack
                        component={Paper}
                        borderRadius={getPxAsRem(8)}
                        width={1}
                        height={1}
                        overflow={'hidden'}
                        flex={1}
                    >
                        {isSmScreen ? (
                            <>
                                <Select
                                    value={filters.processStatusGroup}
                                    onChange={(e) =>
                                        changeTab(
                                            e.target.value as ProcessStatusGroup
                                        )
                                    }
                                    fullWidth
                                    sx={{ padding: 2 }}
                                >
                                    <MenuItem
                                        value={ProcessStatusGroup.Submitted}
                                    >
                                        {getTabLabel(
                                            ProcessStatusGroup.Submitted
                                        )}
                                    </MenuItem>
                                    <MenuItem
                                        value={ProcessStatusGroup.InProcess}
                                    >
                                        {getTabLabel(
                                            ProcessStatusGroup.InProcess
                                        )}
                                    </MenuItem>
                                    <MenuItem
                                        value={ProcessStatusGroup.OutOfProcess}
                                    >
                                        {getTabLabel(
                                            ProcessStatusGroup.OutOfProcess
                                        )}
                                    </MenuItem>
                                </Select>
                                <RosterCandidatesTablePaginated
                                    data={sqlData}
                                    loading={sqlLoading}
                                    totalItems={totalItems}
                                    page={page}
                                    limit={limit}
                                    onPaginationChange={(model) => {
                                        setPage(model.page + 1)
                                        if (model.pageSize !== limit)
                                            setLimit(model.pageSize)
                                    }}
                                />
                            </>
                        ) : (
                            <>
                                <Tabs
                                    value={filters.processStatusGroup}
                                    onChange={(_, newValue) =>
                                        changeTab(newValue)
                                    }
                                    variant="fullWidth"
                                >
                                    <Tab
                                        label={getTabLabel(
                                            ProcessStatusGroup.Submitted
                                        )}
                                        value={ProcessStatusGroup.Submitted}
                                    />
                                    <Tab
                                        label={getTabLabel(
                                            ProcessStatusGroup.InProcess
                                        )}
                                        value={ProcessStatusGroup.InProcess}
                                    />
                                    <Tab
                                        label={getTabLabel(
                                            ProcessStatusGroup.OutOfProcess
                                        )}
                                        value={ProcessStatusGroup.OutOfProcess}
                                    />
                                </Tabs>
                                <RosterCandidatesTablePaginated
                                    data={sqlData}
                                    loading={sqlLoading}
                                    totalItems={totalItems}
                                    page={page}
                                    limit={limit}
                                    onPaginationChange={(model) => {
                                        setPage(model.page + 1)
                                        if (model.pageSize !== limit)
                                            setLimit(model.pageSize)
                                    }}
                                />
                            </>
                        )}
                    </Stack>
                </Stack>
            </RosterCandidateColumnsProvider>
        </BulkActionContextProvider>
    )
}
export default CandidatesSearchTable
